import type { ComponentProps } from 'react';
import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { FullWindowOverlay } from 'react-native-screens';

import { COLORS, ELEVATIONS, RADIUS, SPACING } from '@garnish/constants';
import { useBreakpointHelpers } from '../../../hooks';
import { BodyText } from '../../Text';
import { NOTICE_BANNER_PADDING, NoticeBanner } from '../NoticeBanner';
import type { NoticeBannersStackItems } from './NoticeBannersStackProvider';
import { useNoticeBannersStackContext } from './NoticeBannersStackProvider';

// ────────────────────────────────────────────────────────────────────────────────

export const NoticeBannersStack = (props: NoticeBannersStackProps) => {
  const {
    targetId,
    fullWindowOverlay,
    bannerPadding = NOTICE_BANNER_PADDING,
    style,
    ...restProps
  } = props;

  const { currentBreakpoint } = useBreakpointHelpers();
  const { stack, pop } = useNoticeBannersStackContext();
  const filteredStack = stack.filter((banner) => banner.targetId === targetId);
  const activeBanner = filteredStack[0];

  if (!activeBanner) return null;

  const {
    id,
    onClose,
    autoHideTimeout = 'auto',
    ...restBannerProps
  } = activeBanner;

  // ─── HELPERS ─────────────────────────────────────────────────────

  const popActiveBanner = () => {
    pop();
    onClose?.();
  };

  // ─────────────────────────────────────────────────────────────────

  const Wrapper = fullWindowOverlay ? FullWindowOverlay : View;

  return (
    <Wrapper
      style={[style, currentBreakpoint.isMobile && styles.wrapperXSmall]}
      {...restProps}
    >
      <NoticeBanner
        key={id}
        autoHideTimeout={autoHideTimeout}
        onClose={popActiveBanner}
        padding={bannerPadding}
        {...restBannerProps}
      />
      <NoticeBannersStackMoreBanners stack={filteredStack} />
    </Wrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const NoticeBannersStackMoreBanners = (props: {
  readonly stack: NoticeBannersStackItems;
}) => {
  const { currentBreakpoint } = useBreakpointHelpers();
  const { stack } = props;

  const wrapperResponsiveStyles = currentBreakpoint.isMobile
    ? styles.moreBannersXSmall
    : styles.moreBannersSmallUp;
  const wrapperStyle = [styles.moreBanners, wrapperResponsiveStyles];

  return stack.length > 1 ? (
    <View style={wrapperStyle}>
      <View style={styles.moreBannersContent}>
        <BodyText size={5} style={styles.moreBannersText}>
          +{stack.length - 1}
        </BodyText>
      </View>
    </View>
  ) : null;
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapperXSmall: {
    flexDirection: 'column-reverse',
  },
  moreBanners: {
    alignItems: 'center',
  },
  moreBannersXSmall: {
    marginBottom: SPACING.medium,
  },
  moreBannersSmallUp: {
    marginTop: SPACING.medium,
  },
  moreBannersContent: {
    minWidth: 45,
    backgroundColor: COLORS.NEUTRAL_7,
    borderRadius: RADIUS.medium,
    padding: SPACING.small,
    ...ELEVATIONS['4'],
  },
  moreBannersText: {
    textAlign: 'center',
  },
});

// ────────────────────────────────────────────────────────────────────────────────

type NoticeBannersStackProps = {
  readonly targetId?: string;
  readonly fullWindowOverlay?: boolean;
  readonly bannerPadding?: ComponentProps<typeof NoticeBanner>['padding'];
} & ViewProps;
